import React from 'react';
import { Card, CardMedia } from '@mui/material';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';

export interface VideoDisplayComponent extends BaseComponent {
  type: 'videoDisplay';
  props: VideoDisplayProps;
}

export interface VideoDisplayProps {
  src: string;
  assetid: string;
  resolvedsrc: string;
  captionsrc?: string[];
  captionlang?: string[];
}

export const VideoDisplay: React.FC<VideoDisplayProps> = ({
  src,
  assetid,
  resolvedsrc,
  captionsrc,
  captionlang,
}) => {
  // resolvedsrc may be an external URL to Youtube or similar or it may be a file
  // we have to render different contents: Youtube as iFrame, .mp4 as video

  // presigned URLs contain the actual URL incl. some query params separated from base URL by ?
  // remove them first:
  const pureUrl = resolvedsrc ? resolvedsrc.split('?')[0] : '';
  // then check for file extension of actual file
  const splittedUrl = pureUrl ? pureUrl.split('.') : [''];
  const embed =
    splittedUrl.length < 2 ||
    !['mp4', 'avi', 'mov', 'wmv'].includes(splittedUrl[splittedUrl.length - 1]);
  const mov = ['mov'].includes(splittedUrl[splittedUrl.length - 1]);

  //convert base64 encoded strings to urls
  captionsrc?.forEach((element, index) => {
    if (element.substring(0, 3) !== 'http') {
      element = window.atob(element);
      var subBlob = new Blob([element]);
      captionsrc[index] = URL.createObjectURL(subBlob);
    }
  });

  if (embed) {
    const link = typeof resolvedsrc !== 'undefined' ? resolvedsrc : '';

    // differentiate between the possible 4 kinds of youtube links

    const youtubeStandardLinkFormat = 'https://www.youtube.com/watch?v=';
    const youtubeShortLinkFormat = 'https://youtu.be/';
    const youtubeEmbeddedLinkFormat = 'https://www.youtube.com/embed/';
    const youtubeNoCookieEmbeddedLinkFormat =
      'https://www.youtube-nocookie.com/embed/';

    const linkFormats: string[] = [
      youtubeStandardLinkFormat,
      youtubeShortLinkFormat,
      youtubeEmbeddedLinkFormat,
      youtubeNoCookieEmbeddedLinkFormat,
    ];

    const formatIndex = linkFormats.reduce(
      (formatIndex: number, start: string, index: number) => {
        formatIndex = link.startsWith(start) ? index : formatIndex;
        return formatIndex;
      },
      -1
    );
    const isYoutubeLink = formatIndex !== -1;
    const sourceUrl = isYoutubeLink
      ? youtubeNoCookieEmbeddedLinkFormat +
        link.split(linkFormats[formatIndex])[1].split('&')[0]
      : link;

    return (
      <Card
        sx={{
          position: 'relative',
          paddingTop: '56.25%' /* Player ratio: 100 / (1280 / 720) */,
        }}
      >
        <CardMedia
          width="100%"
          height="100%"
          component="iframe"
          image={sourceUrl}
          frameBorder="0"
          sx={{ position: 'absolute', top: 0, left: 0 }}
          key={assetid ?? src}
        />
      </Card>
    );
  } else {
    return (
      <video controls width="100%" height="100%">
        <source src={mov ? src : resolvedsrc} type="video/mp4" />
        {captionlang &&
          captionsrc?.map((element, index) => (
            <track
              key={index}
              label={captionlang[index]}
              kind="subtitles"
              srcLang={captionlang[index]}
              src={element}
              default
            />
          ))}
      </video>
    );
  }
};

export const renderVideoDisplayAction = assign(
  (context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    newContext.widgetData.push({
      type: 'videoDisplay',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        src: payload.src,
        assetid: payload.assetid,
        resolvedsrc: payload.resolvedsrc,
      },
    });
    return newContext;
  }
);
